/**
 * Parse a server timestamp into a Date with correct timezone handling.
 *
 * The backend emits naive-UTC datetimes with NO timezone designator (no `Z`).
 * Parsing those with a bare `new Date()` makes the browser interpret them as
 * LOCAL time, so the UI shows the UTC wall-clock (off by the viewer's offset —
 * e.g. Recent Imports / Data Sync showing GMT). We fix that by treating a naive
 * *datetime* as UTC (append `Z`), then formatting to local.
 *
 * A naive *date-only* value ("2027-05-01") is instead parsed as a LOCAL calendar
 * date, because `new Date("2027-05-01")` would treat it as UTC midnight and can
 * render the day before in negative-offset zones (Mountain Time). Mirrors the
 * datetime half of utils/importStatus.ts → parseServerDate.
 */
export function parseServerDate(isoString: string | null | undefined): Date | null {
  if (!isoString) return null
  const hasTime = /[T ]\d{2}:\d{2}/.test(isoString)
  if (!hasTime) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoString)
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  }
  const hasTz = /([zZ])|([+-]\d{2}:?\d{2})$/.test(isoString)
  const d = new Date(hasTz ? isoString : `${isoString}Z`)
  return isNaN(d.getTime()) ? null : d
}

export function formatLocalDateTime(isoString: string | null | undefined): string {
  const d = parseServerDate(isoString)
  if (!d) return '—'
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatLocalDate(isoString: string | null | undefined): string {
  const d = parseServerDate(isoString)
  if (!d) return '—'
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function formatRelativeTime(isoString: string | null | undefined): string {
  const d = parseServerDate(isoString)
  if (!d) return '—'
  const diff = Date.now() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return formatLocalDate(isoString)
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`
}
